import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../../misc/button/Button";
import { AuthContext } from "../../context/AuthProviderWrapper";
import { DeclarationContext } from "../../context/DeclarationWrapper";
import { BACKEND_URL } from "../../../config/config";
import { useNavigate } from "react-router-dom";
import EditDeclarationModal from "../declarationDetail-CRUD/EditDeclarationModal";
import { NotificationContext } from "../../context/NotificationWrapper";
import { Spin, Modal } from "antd";
import DsnFileUpload from "../../misc/DsnFileUpload";

const monthsData = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

function DeclarationSignature() {
  const { declaration, updateDeclaration, dsnFile, isAttestation, refreshAvailablePeriods, refreshPeriodsForSelects } = useContext(DeclarationContext);
  const { user, activeCompany } = useContext(AuthContext);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isEditableFlag, setIsEditableFlag] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState([]);
  const navigate = useNavigate();
  const { openNotification } = useContext(NotificationContext);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // Détecter si c'est une déclaration annuelle ou semestrielle
  const isAnnual = declaration?.isAnnual || false;
  const isSemiAnnual = declaration?.isSemiAnnual || false;
  const semester = declaration?.semester || null;

  useEffect(() => {
    updateDeclaration((prev) => ({
      ...prev,
      membershipCode: activeCompany.membershipCode,
      // Préserver isAnnual et isSemiAnnual s'ils étaient déjà définis
      isAnnual: prev?.isAnnual || false,
      isSemiAnnual: prev?.isSemiAnnual || false,
      semester: prev?.semester || null,
    }));
  }, [activeCompany]);

  const abortControllerRef = useRef(null);

  const checkIfEditable = (declaration) => {
    if (!declaration?.id || (declaration?.payed === "no" && user.isAdmin)) {
      return true;
    }
    return false;
  };

  const handleEdit = (record) => {
    const flag = checkIfEditable(record);
    setIsEditableFlag(flag);
    setEditModalVisible(true);
  };

  const handleEditModalCancel = () => {
    setEditModalVisible(false);
    setIsEditableFlag(false);
  };

  const handleCreate = () => {
    setConfirmModalVisible(true);
  }

  const handleCancelSubmit = () => {
    setConfirmModalVisible(false);
  };

  const handleConfirmSubmit  = () => {
    setConfirmModalVisible(false);
    setIsLoading(true);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setError({});

    // Si c'est une attestation, appeler l'endpoint attestation-request/create
    if (isAttestation(declaration) && dsnFile) {
      handleSubmitAttestation();
    } else {
      handleSubmitDeclaration();
    }
  };

  const handleSubmitAttestation = () => {
    // Construire inputedDate selon le type de période
    let dateStr;
    if (isAnnual) {
      dateStr = `${declaration.year}-12-01`;
    } else if (isSemiAnnual && semester) {
      const month = semester === 1 ? '06' : '12';
      dateStr = `${declaration.year}-${month}-01`;
    } else {
      dateStr = `${declaration.year}-${String(declaration.month).padStart(2, '0')}-01`;
    }
    const inputedDate = Math.floor(new Date(dateStr).getTime() / 1000);

    // Déterminer le periodType
    let periodType = 'monthly';
    if (isAnnual) {
      periodType = 'annual';
    } else if (isSemiAnnual && semester) {
      periodType = semester === 1 ? 'semi_annual_s1' : 'semi_annual_s2';
    }

    // Préparer les données avec inputedDate, membershipCategory et periodType
    const dataWithDate = {
      salaryWorker: declaration?.salaryWorker,
      salaryEtam: declaration?.salaryEtam,
      salaryExecutive: declaration?.salaryExecutive,
      workForce: declaration?.workForce,
      month: declaration?.month,
      year: declaration?.year,
      agreeOptionalWorker: declaration?.agreeOptionalWorker,
      membershipCode: activeCompany.membershipCode,
      inputedDate: inputedDate,
      membershipCategory: declaration?.membershipCategory,
      periodType: periodType
    };

    // Créer FormData pour envoyer le fichier avec les données JSON
    const formData = new FormData();
    formData.append('dsnFile', dsnFile);
    formData.append('data', JSON.stringify(dataWithDate));

    axios
      .post(`${BACKEND_URL}/api/attestation-request/create`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        signal: abortControllerRef.current?.signal,
      })
      .then((res) => {
        const data = res.data.data || res.data;
        openNotification("Succès", "Demande d'attestation soumise avec succès. Elle sera examinée par l'APAS-BTP.", "success");
        refreshAvailablePeriods();
        navigate("/declaration-certificate-payment", {
          state: { message: data, isAttestationRequest: true },
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }
        const errorMessage = error.response?.data?.message || "Une erreur s'est produite";
        const errorDescription = error.response?.data?.description || "";
        const content = (
          <>
            <div style={{ fontWeight: 'bold' }}>{errorMessage}</div>
            {errorDescription && <div style={{ marginTop: '5px' }}>{errorDescription}</div>}
          </>
        );
        openNotification("Erreur", content, "error");
        console.error("error", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitDeclaration = () => {
    const requestBody = {
      salaryWorker: declaration?.salaryWorker,
      salaryEtam: declaration?.salaryEtam,
      salaryExecutive: declaration?.salaryExecutive,
      workForce: declaration?.workForce,
      month: declaration?.month,
      year: declaration?.year,
      agreeOptionalWorker: declaration?.agreeOptionalWorker,
      membershipCode: activeCompany.membershipCode,
      isAnnual: isAnnual,
      semester: isSemiAnnual ? semester : null,
    };

    axios
      .post(`${BACKEND_URL}/api/declaration/submit`, requestBody, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        signal: abortControllerRef.current?.signal,
      })
      .then((res) => {
        const data = res.data.data;
        if (data.id) {
          let message;
          if (data.feeAmount === 0) {
            message = "Attestation créée avec succès";
          } else if (isAnnual) {
            message = "Régularisation annuelle créée avec succès";
          } else if (isSemiAnnual) {
            message = `Régularisation semestrielle S${semester} créée avec succès`;
          } else {
            message = "Déclaration créée avec succès";
          }
          openNotification("Succès", message, "success");
        }
        return data;
      })
      .then((res) => {
        refreshAvailablePeriods();
        refreshPeriodsForSelects();
        navigate("/declaration-certificate-payment", {
          state: { message: res },
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }
        const errorMessage = error.response.data.message || "Une erreur s'est produite";
        const errorDescription = error.response.data.description || "";
        const content = (
          <>
            <div style={{ fontWeight: 'bold' }}>{errorMessage}</div>
            {errorDescription && <div style={{ marginTop: '5px' }}>{errorDescription}</div>}
          </>
        );
        openNotification("Erreur", content, "error");
        console.error("error", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="block-right">
      <Spin spinning={isLoading} fullscreen />
      <div className="info-declaration">
        <h4 style={{ textTransform: "uppercase" }}>
        {isAnnual
          ? "Régularisation annuelle"
          : isSemiAnnual
            ? `Régularisation semestrielle S${semester}`
            : "Déclaration de salaires"}
        {declaration?.inputedDdate > 0 ? (
            ' pour ' + new Intl.DateTimeFormat('fr-FR', {
              month: 'long',
              year: 'numeric'
            }).format(new Date(declaration.inputedDdate * 1000))
          ) : ''}
        </h4>
        <div>
          <div>
            Période :{" "}
            <b>
              {isAnnual
                ? `Année ${declaration?.year}`
                : isSemiAnnual
                  ? `S${semester} ${declaration?.year} (${semester === 1 ? 'Janvier - Juin' : 'Juillet - Décembre'})`
                  : `${declaration?.month}/${declaration?.year}`}
            </b>
          </div>
          <div>
            Montant dû : <b>{declaration?.feeAmountLocale} </b>
          </div>
        </div>
      </div>
      <div className="info-signature">
        <h4 style={{ textTransform: "uppercase" }}>
          Signer et envoyer le document{" "}
        </h4>
        <p
          style={{ fontStyle: "italic", fontWeight: "700", marginTop: "20px" }}
        >
          A ce stade, vous pouvez modifier les données saisies préalablement en
          cliquant sur le bouton « Modifier » ou valider les données en signant
          électroniquement ce document. En cliquant sur « Signer et Envoyer »,
          vous transmettez le document.
          <br />
          <br />
{isAttestation(declaration)
            ? "Votre demande d'attestation sera examinée par l'APAS-BTP. Vous recevrez une notification dès qu'elle sera validée et votre attestation vous sera transmise en ligne. Sans accusé de réception, la présente procédure ne peut être considérée comme achevée."
            : "Une facture vous sera transmise en ligne aussitôt après la vérification de la signature. Sans accusé de réception, la présente procédure ne peut être considérée comme achevée."
          }
        </p>

        {/* Affichage et modification du fichier DSN pour les attestations */}
        {isAttestation(declaration) && (
          <DsnFileUpload />
        )}

        <div className="button-container">
          <Button children="Modifier" onClick={() => handleEdit(declaration)} />
          <Button onClick={handleCreate} children="Signer et envoyer" />

          <EditDeclarationModal
            open={editModalVisible}
            onCancel={handleEditModalCancel}
            record={declaration}
            mode={isEditableFlag ? "edit" : "view"}
            navigateTo="/declaration-summary"
            isPreviewMode={true}
            isAnnual={isAnnual}
            isSemiAnnual={isSemiAnnual}
            semester={semester}
          />
        </div>
      </div>
      <Modal
        title="Confirmation de signature"
        open={confirmModalVisible}
        onOk={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
        okText="Signer et envoyer"
        cancelText="Annuler"
        centered={true}
        okButtonProps={{ style: { backgroundColor: '#ee463b', borderColor: '#ee463b' } }}
        className="confirmation-modal"
      >
        <div>
          <p>Êtes-vous sûr de vouloir signer et envoyer cette déclaration ?</p>
          <p style={{ fontWeight: 'bold', fontSize: 20 }}>
            Période : {(() => {
              if (isAnnual) {
                return `Année ${declaration?.year || '(Année non définie)'}`;
              } else if (isSemiAnnual) {
                const semesterLabel = semester === 1 ? 'S1 (Janvier - Juin)' : 'S2 (Juillet - Décembre)';
                return `${semesterLabel} ${declaration?.year || '(Année non définie)'}`;
              } else {
                // Convertir la valeur du mois en nombre pour éliminer le zéro en tête
                const monthNumber = declaration?.month ? parseInt(declaration.month, 10).toString() : null;
                const monthLabel = monthNumber
                  ? monthsData.find(m => m.value === monthNumber)?.label || '(Mois inconnu)'
                  : '(Mois non défini)';

                return `${monthLabel} ${declaration?.year || '(Année non définie)'}`;
              }
            })()}
          </p>
          <p style={{ fontWeight: 'bold', fontSize: 20 }}>
            Montant dû : {declaration?.feeAmountLocale || `${declaration?.feeAmount || 0} €`}
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default DeclarationSignature;
